// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import {
  HistoryBoundary,
  RecoveryBanner,
} from '@/components/remote/timeline/RecoveryBanner';
import { RunSection } from '@/components/remote/timeline/RunSection';
import { MorphIcon } from '@/components/ui/morph-icon';
import type {
  ProjectedArtifact,
  ProjectedArtifactManifest,
} from '@web/lib/projector';
import type { ActivityTimelineModel } from '@web/lib/remoteActivityModel';
import type { HumanInputItem } from '@web/lib/remoteWorkItemModel';
import { MessageSquareText } from 'lucide';

export function RunTimeline({
  model,
  onAnswerHumanInput,
  answeredHumanInteractionIds,
  artifactsByRun,
  artifactManifestsByRun,
  onOpenArtifact,
  defaultWorkExpanded = false,
}: {
  model: ActivityTimelineModel;
  onAnswerHumanInput?: (item: HumanInputItem) => void;
  answeredHumanInteractionIds?: ReadonlySet<string>;
  artifactsByRun?: Record<string, ProjectedArtifact[]>;
  artifactManifestsByRun?: Record<string, ProjectedArtifactManifest>;
  onOpenArtifact?: (artifact: ProjectedArtifact) => void;
  defaultWorkExpanded?: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      <RecoveryBanner sync={model.sync} />
      <HistoryBoundary sync={model.sync} />
      {model.runs.length === 0 ? (
        <section className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl bg-ds-bg-neutral-subtle-default p-8 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ds-bg-neutral-default-default text-ds-icon-neutral-muted-default">
            <MorphIcon icon={MessageSquareText} className="h-5 w-5" />
          </div>
          <p className="mt-3 text-body-sm font-semibold text-ds-text-neutral-default-default">
            Waiting for desktop activity
          </p>
          <p className="mt-1 max-w-sm text-body-sm text-ds-text-neutral-muted-default">
            New runs and completed steps will appear here in order.
          </p>
        </section>
      ) : (
        <div
          className="flex flex-col gap-3"
          role="list"
          aria-label="Project run timeline"
        >
          {model.runs.map((run) => (
            <RunSection
              key={run.id}
              run={run}
              current={run.id === model.activeRunId}
              defaultExpanded={defaultWorkExpanded}
              onAnswerHumanInput={onAnswerHumanInput}
              answeredHumanInteractionIds={answeredHumanInteractionIds}
              artifacts={artifactsByRun?.[run.id] || []}
              artifactManifest={artifactManifestsByRun?.[run.id]}
              onOpenArtifact={onOpenArtifact}
            />
          ))}
        </div>
      )}
    </div>
  );
}
